import { useState, useEffect, useRef, useContext } from "react";
import { WellContext } from "../context/WellContext";
import TransmitterFaceplate from "./TransmitterFaceplate";
import { useTrend } from "../context/TrendContext";
import { saveTrendPoint } from "../database/historianDB";

function Transmitter({
  tag,
  value,
  x,
  y,

  unit = "",
  min = 0,
  max = 100,
  HH = 90,
  H = 75,
  L = 25,
  LL = 10,
  description = "",
  c = "",

  scale = 1,
  closedValue,
  MinValue = 0.003,
}) {

  // ================= FACEPLATE =================
  const [showFaceplate, setShowFaceplate] = useState(false);

  // ================= INITIAL STATE =================
  const [displayValue, setDisplayValue] = useState(() => {
    const saved = localStorage.getItem(`transmitter_${tag}`);
    const parsed = Number(saved);
    return !isNaN(parsed) && parsed > 0 ? parsed : MinValue;
  });

  const { valves } = useContext(WellContext);

  const isDHSVClosed = valves?.DHSV_10101;
  const isSSSVClosed = valves?.SSSV_10102;
  const isWellShut = isSSSVClosed || isDHSVClosed;

  const intervalRef = useRef(null);


const { logValue } = useTrend();

// ======================================================
// HISTORIAN + TREND LOGGER (1s DCS SAMPLING)
// ======================================================

const lastLogTimeRef = useRef(0);

useEffect(() => {

  const now = Date.now();

  // ==============================================
  // ONLY LOG EVERY 1 SECOND (VERY IMPORTANT)
  // ==============================================

  if (now - lastLogTimeRef.current < 1000) return;

  lastLogTimeRef.current = now;

  if (!tag || isNaN(displayValue)) return;

  // ==============================================
  // LIVE TREND CONTEXT (FAST MEMORY UPDATE)
  // ==============================================

  if (now - lastLogTimeRef.current < 1000) {
  logValue(tag, displayValue);
}

  // ==============================================
  // HISTORIAN STORAGE (INDEXEDDB)
  // ==============================================

  saveTrendPoint({
    tag: tag,
    value: Number(displayValue),
    timestamp: now
  });

}, [displayValue, tag]);




  // ================= SAFE INPUT =================
  const safeValue = Number(value);

  const baseValue =
    !isNaN(safeValue) && safeValue !== null
      ? safeValue * scale
      : MinValue;

  // ================= SPECIAL LOGIC =================
  let adjustedTarget = baseValue;

  if (tag === "PT10106" || tag === "TT10106") {
    const chkOpening =
      Number(localStorage.getItem("CHK_101_opening")) || 0;

    adjustedTarget = baseValue * (chkOpening / 100);
  }

  // ================= VALVE LOGIC =================
  const controllingValveOpen = !isWellShut;

  // ================= DYNAMIC UPDATE =================
  useEffect(() => {

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {

      setDisplayValue(prev => {

        let next;

        if (controllingValveOpen) {
          const diff = adjustedTarget - prev;
          next = prev + diff * 0.1;

          if (Math.abs(diff) < 0.01) {
            next = adjustedTarget;
          }

        } else {

          if (tag === "TT10102") {
            next = prev + (22 - prev) * 0.1;

          } else if (tag === "PT10106") {
            next = prev + (0.003 - prev) * 0.1;

          } else if (tag === "TT10106") {
            next = prev + (22 - prev) * 0.1;

          } else {
            next = prev * 0.85;

            if (next <= MinValue) {
              next = MinValue;
            }
          }
        }

        if (isNaN(next) || next <= 0) {
          return MinValue;
        }

        return next;

      });

    }, 300);

    return () => clearInterval(intervalRef.current);

  }, [controllingValveOpen, adjustedTarget]);

  // ================= SAVE LIVE VALUE =================
  useEffect(() => {
    if (!isNaN(displayValue)) {
      localStorage.setItem(`transmitter_${tag}`, displayValue);
      saveTrendPoint(`transmitter_${tag}`, displayValue);

      // 🔥 NEW: Save for TrendGraph direct access
      localStorage.setItem(tag, displayValue);
      saveTrendPoint(tag, displayValue);
    }
  }, [displayValue, tag]);

  // ================= SAVE CONFIG (ONLY ONCE) =================
  useEffect(() => {
    if (!tag) return;

    // Prevent overwrite
    if (!localStorage.getItem(`min_${tag}`)) {
      localStorage.setItem(`min_${tag}`, min);
    }

    if (!localStorage.getItem(`max_${tag}`)) {
      localStorage.setItem(`max_${tag}`, max);
    }

    if (!localStorage.getItem(`HH_${tag}`)) {
      localStorage.setItem(`HH_${tag}`, HH);
    }

    if (!localStorage.getItem(`H_${tag}`)) {
      localStorage.setItem(`H_${tag}`, H);
    }

    if (!localStorage.getItem(`L_${tag}`)) {
      localStorage.setItem(`L_${tag}`, L);
    }

    if (!localStorage.getItem(`LL_${tag}`)) {
      localStorage.setItem(`LL_${tag}`, LL);
    }

    if (!localStorage.getItem(`description_${tag}`)) {
      localStorage.setItem(`description_${tag}`, description);
    }

  }, [tag]);


  // ================= FORMAT =================
  const formattedValue = isNaN(displayValue)
    ? MinValue.toFixed(3)
    : displayValue.toFixed(3);

  // ================= RENDER =================
  return (
    <>
      {/* ========= TRANSMITTER UI ========= */}
      <div
        className="tx-ui"
        style={{
          position: "absolute",
          left: x,
          top: y,
          padding: "4px 6px",
          borderRadius: "4px",
          fontSize: "10px",
          fontWeight: "bold",
          cursor: "pointer",
          color: c,
        }}
        onClick={() => setShowFaceplate(true)}
      >
        {formattedValue} {unit}
      </div>

      {/* ========= FACEPLATE ========= */}
      {showFaceplate && (
        <TransmitterFaceplate
          tag={tag}
          value={displayValue}
          unit={unit}
          min={min}
          max={max}
          HH={HH}
          H={H}
          L={L}
          LL={LL}
          description={description}
          x={x + 40}
          y={y + 40}
          onClose={() => setShowFaceplate(false)}
        />
      )}
    </>
  );
}

export default Transmitter;